import {
    createContext,
    useCallback,
    useContext,
    useRef,
    type ComponentPropsWithRef,
    type KeyboardEvent,
    type ReactNode,
} from 'react';
import { cn } from '../lib/cn';
import { disabledStyles, focusRing } from '../lib/styles';

export type OptionRowType = 'radio' | 'check';

type OptionGroupContextValue = {
    type: OptionRowType;
    register: (el: HTMLButtonElement | null, index: number) => void;
    onKeyNav: (event: KeyboardEvent<HTMLButtonElement>, index: number) => void;
    /** Roving tabindex: only the checked row (or the first) is tabbable in a radio group. */
    isTabbable: (index: number, checked: boolean) => boolean;
};

const OptionGroupContext = createContext<OptionGroupContextValue | null>(null);

export type OptionGroupProps = Omit<ComponentPropsWithRef<'div'>, 'onChange'> & {
    type: OptionRowType;
    /** The question text, or the id of the element holding it. */
    label?: string;
    labelledBy?: string;
    /**
     * Radio groups only: whether any option is selected. Drives the roving tabindex
     * (selected row tabbable, otherwise the first row). Leave it out and every row
     * stays tabbable — correct, just more tab stops.
     */
    hasSelection?: boolean;
    children: ReactNode;
};

/**
 * Wraps a set of `OptionRow`s.
 *
 * - `radio` gets `role="radiogroup"` plus arrow-key navigation and a roving
 *   tabindex, so the group is one tab stop — the standard radio pattern.
 * - `check` gets `role="group"`; every row is its own tab stop.
 */
export function OptionGroup({
    type,
    label,
    labelledBy,
    hasSelection,
    className,
    children,
    ...rest
}: OptionGroupProps) {
    const rows = useRef<(HTMLButtonElement | null)[]>([]);

    const register = useCallback((el: HTMLButtonElement | null, index: number) => {
        rows.current[index] = el;
    }, []);

    const onKeyNav = useCallback(
        (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
            if (type !== 'radio') return;
            const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
            if (!keys.includes(event.key)) return;

            const items = rows.current.filter((el): el is HTMLButtonElement =>
                Boolean(el && !el.disabled),
            );
            if (items.length === 0) return;

            const current = items.findIndex((el) => el === rows.current[index]);
            let next = current;
            if (event.key === 'ArrowDown' || event.key === 'ArrowRight')
                next = (current + 1) % items.length;
            if (event.key === 'ArrowUp' || event.key === 'ArrowLeft')
                next = (current - 1 + items.length) % items.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = items.length - 1;

            event.preventDefault();
            const target = items[next];
            target?.focus();
            // Matching the design: moving with arrows selects, like a native radio group.
            target?.click();
        },
        [type],
    );

    const isTabbable = useCallback(
        (index: number, checked: boolean) => {
            if (type !== 'radio' || hasSelection === undefined) return true;
            return hasSelection ? checked : index === 0;
        },
        [type, hasSelection],
    );

    return (
        <OptionGroupContext.Provider value={{ type, register, onKeyNav, isTabbable }}>
            <div
                role={type === 'radio' ? 'radiogroup' : 'group'}
                aria-label={labelledBy ? undefined : label}
                aria-labelledby={labelledBy}
                className={cn('flex flex-col gap-[2px] px-[10px] py-2', className)}
                {...rest}
            >
                {children}
            </div>
        </OptionGroupContext.Provider>
    );
}

export type OptionRowProps = Omit<
    ComponentPropsWithRef<'button'>,
    'children' | 'type' | 'role' | 'aria-checked' | 'onSelect'
> & {
    /** Falls back to the group's type when inside an `OptionGroup`. */
    type?: OptionRowType;
    checked: boolean;
    /**
     * Tapping the selected value clears it (radio) or removes it from the selection
     * (check) — so the handler receives the value the row would move to.
     */
    onSelect: (next: boolean) => void;
    /** Position in the group; required for arrow-key navigation in a radio group. */
    index?: number;
    children: ReactNode;
};

/**
 * A full answer row: 44px tall, the whole surface is the hit area, 12px between
 * marker and label (principle 1 — no small radio dots on site).
 *
 * Markers, exactly as specified:
 * - unselected: white, 1.5px `--border-dashed`
 * - check, selected: `--accent` fill, radius 5, `inset 0 0 0 2.5px` white
 * - radio, selected: white core with a 6px `--accent` ring, radius 50%
 */
export function OptionRow({
    type,
    checked,
    onSelect,
    index = 0,
    className,
    children,
    onClick,
    onKeyDown,
    disabled,
    ...rest
}: OptionRowProps) {
    const group = useContext(OptionGroupContext);
    const resolvedType = type ?? group?.type ?? 'radio';
    const isCheck = resolvedType === 'check';

    return (
        <button
            type="button"
            role={isCheck ? 'checkbox' : 'radio'}
            aria-checked={checked}
            disabled={disabled}
            tabIndex={group ? (group.isTabbable(index, checked) ? 0 : -1) : 0}
            ref={(el) => {
                group?.register(el, index);
            }}
            onClick={(event) => {
                onClick?.(event);
                if (event.defaultPrevented) return;
                onSelect(!checked);
            }}
            onKeyDown={(event) => {
                onKeyDown?.(event);
                if (event.defaultPrevented) return;
                group?.onKeyNav(event, index);
            }}
            className={cn(
                'flex w-full min-h-11 cursor-pointer items-center gap-3 rounded-[7px] border-0 px-3 py-[11px]',
                'font-[family-name:var(--font-body)] text-left text-[14px] leading-[1.35]',
                'transition-colors duration-100',
                checked
                    ? 'bg-[var(--accent-tint)] font-medium text-[var(--accent-dark)]'
                    : 'bg-[var(--surface-card)] font-normal text-[var(--text-primary)] hover:bg-[var(--surface-page)]',
                focusRing,
                disabledStyles,
                className,
            )}
            {...rest}
        >
            <span
                aria-hidden="true"
                className={cn(
                    'box-border h-5 w-5 shrink-0',
                    isCheck ? 'rounded-[5px]' : 'rounded-full',
                    checked
                        ? isCheck
                            ? 'border-[1.5px] border-[var(--accent)] bg-[var(--accent)] shadow-[inset_0_0_0_2.5px_var(--surface-card)]'
                            : 'border-[6px] border-[var(--accent)] bg-[var(--surface-card)]'
                        : 'border-[1.5px] border-[var(--border-dashed)] bg-[var(--surface-card)]',
                )}
            />
            <span className="min-w-0 flex-1 text-left">{children}</span>
        </button>
    );
}
